import { useState, useEffect, useCallback } from 'react';import { Layout } from '../../components/Layout/Layout';
import { Card, CardHeader, Spinner, Button } from '../../components/UI';
import { Assessment, Client } from '../../types';
import { getAssessmentsByClient, regenerateReport } from '../../services/assessments';
import { getClients } from '../../services/clients';
import {
  DocumentArrowDownIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  ArrowTrendingUpIcon,
  ChartBarIcon,
  CloudArrowDownIcon,
} from '@heroicons/react/24/outline';

type Route = '/' | '/callback' | '/reports' | '/network';

interface ReportsPageProps {
  onNavigate?: (route: Route) => void;
}

const formatCurrency = (v: number) =>
  v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('es-MX', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const formatMonth = (iso: string) =>
  new Date(iso).toLocaleDateString('es-MX', { month: 'short', year: '2-digit' });

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; style: React.CSSProperties; icon: React.ReactNode }> = {
    COMPLETED: {
      label: 'Completado',
      style: { background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' },
      icon: <CheckCircleIcon className="h-3.5 w-3.5" />,
    },
    RUNNING: {
      label: 'En progreso',
      style: { background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe' },
      icon: <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />,
    },
    PENDING: {
      label: 'Pendiente',
      style: { background: '#fefce8', color: '#ca8a04', border: '1px solid #fef08a' },
      icon: <ClockIcon className="h-3.5 w-3.5" />,
    },
    FAILED: {
      label: 'Fallido',
      style: { background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' },
      icon: <XCircleIcon className="h-3.5 w-3.5" />,
    },
  };

  const config = map[status] || map['PENDING'];
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold" style={config.style}>
      {config.icon}
      {config.label}
    </span>
  );
}

// ─── Trends Chart ─────────────────────────────────────────────────────────────

interface TrendPoint {
  month: string;
  savings: number;
  date: string;
}

function TrendsChart({ assessments }: { assessments: (Assessment & { results?: { savingsSummary?: { totalMonthlySavings?: number } } })[] }) {
  const completed = assessments
    .filter(a => a.status === 'COMPLETED' && a.results?.savingsSummary?.totalMonthlySavings !== undefined)
    .slice(0, 6)
    .reverse();

  if (completed.length < 2) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-gray-400">
        <ChartBarIcon className="h-12 w-12 mb-3 opacity-30" />
        <p className="text-sm">Se necesitan al menos 2 assessments completados para ver tendencias</p>
      </div>
    );
  }

  const points: TrendPoint[] = completed.map(a => ({
    month: formatMonth(a.startedAt),
    savings: a.results!.savingsSummary!.totalMonthlySavings!,
    date: a.startedAt,
  }));

  const maxSavings = Math.max(...points.map(p => p.savings));
  const minSavings = Math.min(...points.map(p => p.savings));
  const range = maxSavings - minSavings || 1;

  const chartH = 160;
  const chartW = 100;
  const padX = 8;
  const padY = 16;

  const getX = (i: number) => padX + (i / (points.length - 1)) * (chartW - padX * 2);
  const getY = (v: number) => padY + (1 - (v - minSavings) / range) * (chartH - padY * 2);

  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${getX(i)} ${getY(p.savings)}`).join(' ');
  const areaD = `${pathD} L ${getX(points.length - 1)} ${chartH - padY} L ${getX(0)} ${chartH - padY} Z`;

  const lastSavings = points[points.length - 1].savings;
  const prevSavings = points[points.length - 2].savings;
  const trend = lastSavings >= prevSavings ? 'up' : 'down';
  const trendPct = prevSavings > 0 ? Math.abs(((lastSavings - prevSavings) / prevSavings) * 100).toFixed(1) : '0';

  return (
    <div>
      {/* Summary row */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="p-4 rounded-xl text-center" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
          <p className="text-xs text-green-600 font-medium mb-1">Último ahorro identificado</p>
          <p className="text-xl font-bold text-green-700">${formatCurrency(lastSavings)}</p>
        </div>
        <div className="p-4 rounded-xl text-center" style={{ background: '#eff6ff', border: '1px solid #bfdbfe' }}>
          <p className="text-xs font-medium mb-1" style={{ color: '#2563eb' }}>Promedio</p>
          <p className="text-xl font-bold" style={{ color: '#1d4ed8' }}>
            ${formatCurrency(points.reduce((s, p) => s + p.savings, 0) / points.length)}
          </p>
        </div>
        <div className="p-4 rounded-xl text-center" style={{
          background: trend === 'up' ? '#f0fdf4' : '#fef2f2',
          border: `1px solid ${trend === 'up' ? '#bbf7d0' : '#fecaca'}`,
        }}>
          <p className="text-xs font-medium mb-1" style={{ color: trend === 'up' ? '#16a34a' : '#dc2626' }}>
            Tendencia vs anterior
          </p>
          <p className="text-xl font-bold flex items-center justify-center gap-1" style={{ color: trend === 'up' ? '#16a34a' : '#dc2626' }}>
            <ArrowTrendingUpIcon className={`h-5 w-5 ${trend === 'down' ? 'rotate-180' : ''}`} />
            {trendPct}%
          </p>
        </div>
      </div>

      {/* SVG Chart */}
      <div className="relative">
        <svg viewBox={`0 0 ${chartW} ${chartH}`} className="w-full" style={{ height: '180px' }}>
          <defs>
            <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#E05A1C" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#E05A1C" stopOpacity="0.02" />
            </linearGradient>
          </defs>
          {/* Grid lines */}
          {[0.25, 0.5, 0.75].map((f, i) => (
            <line key={i}
              x1={padX} y1={padY + f * (chartH - padY * 2)}
              x2={chartW - padX} y2={padY + f * (chartH - padY * 2)}
              stroke="#e5e7eb" strokeWidth="0.5" strokeDasharray="2,2"
            />
          ))}
          {/* Area fill */}
          <path d={areaD} fill="url(#areaGrad)" />
          {/* Line */}
          <path d={pathD} fill="none" stroke="#E05A1C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          {/* Points */}
          {points.map((p, i) => (
            <g key={i}>
              <circle cx={getX(i)} cy={getY(p.savings)} r="2" fill="#E05A1C" />
              <circle cx={getX(i)} cy={getY(p.savings)} r="4" fill="#E05A1C" fillOpacity="0.2" />
            </g>
          ))}
        </svg>
        {/* X axis labels */}
        <div className="flex justify-between px-2 mt-1">
          {points.map((p, i) => (
            <span key={i} className="text-xs text-gray-400">{p.month}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function ReportsPage({ onNavigate }: ReportsPageProps) {
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [assessments, setAssessments] = useState<(Assessment & { results?: { savingsSummary?: { totalMonthlySavings?: number } } })[]>([]);
  const [loading, setLoading] = useState(false);
  const [clientsLoading, setClientsLoading] = useState(true);

  useEffect(() => {
    getClients()
      .then((data) => {
        setClients(data);
        if (data.length > 0) setSelectedClientId(data[0].id);
      })
      .finally(() => setClientsLoading(false));
  }, []);

  const loadAssessments = useCallback(async (clientId: string) => {
    if (!clientId) return;
    setLoading(true);
    try {
      const data = await getAssessmentsByClient(clientId);
      const sorted = [...data].sort(
        (a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime()
      );
      setAssessments(sorted);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedClientId) loadAssessments(selectedClientId);
  }, [selectedClientId, loadAssessments]);

  const handleReportUrlUpdate = useCallback((id: string, url: string) => {
    setAssessments(prev => prev.map(a => a.id === id ? { ...a, reportUrl: url } : a));
  }, []);
  const selectedClient = clients.find((c) => c.id === selectedClientId);
  const completedAssessments = assessments.filter(a => a.status === 'COMPLETED');

  return (
    <Layout onNavigate={onNavigate}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold" style={{ color: '#1B2B6B' }}>Historial de Reportes</h2>
            <p className="text-sm text-gray-500 mt-0.5">Assessments ejecutados por cliente</p>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => selectedClientId && loadAssessments(selectedClientId)}
          >
            <ArrowPathIcon className="h-4 w-4 mr-1" />
            Actualizar
          </Button>
        </div>

        {/* Client selector */}
        <Card>
          <div className="flex items-center gap-4">
            <label className="text-sm font-semibold whitespace-nowrap" style={{ color: '#1B2B6B' }}>
              Cliente:
            </label>
            {clientsLoading ? (
              <Spinner />
            ) : (
              <select
                value={selectedClientId}
                onChange={(e) => setSelectedClientId(e.target.value)}
                className="flex-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2"
                style={{ borderColor: '#dde4f0', color: '#1B2B6B' }}
              >
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          {selectedClient && (
            <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap gap-4 text-sm text-gray-600">
              <span>
                <span className="font-medium" style={{ color: '#1B2B6B' }}>Región:</span>{' '}
                {selectedClient.region || 'us-east-1'}
              </span>
              <span>
                <span className="font-medium" style={{ color: '#1B2B6B' }}>Schedule:</span>{' '}
                {selectedClient.scheduleEnabled
                  ? <span style={{ color: '#16a34a' }}>✅ Activo (día 10)</span>
                  : <span style={{ color: '#8A8F9A' }}>⏸ Desactivado</span>}
              </span>
              {selectedClient.lastAssessmentAt && (
                <span>
                  <span className="font-medium" style={{ color: '#1B2B6B' }}>Último:</span>{' '}
                  {formatDate(selectedClient.lastAssessmentAt)}
                </span>
              )}
            </div>
          )}
        </Card>

        {/* Trends dashboard */}
        {completedAssessments.length > 0 && (
          <Card>
            <CardHeader title="Tendencias de Ahorro Identificado" />
            <TrendsChart assessments={assessments as (Assessment & { results?: { savingsSummary?: { totalMonthlySavings?: number } } })[]} />
          </Card>
        )}

        {/* Assessments table */}
        <Card>
          <CardHeader title={`Assessments${selectedClient ? ` — ${selectedClient.name}` : ''}`} />

          {loading ? (
            <div className="flex justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : assessments.length === 0 ? (
            <div className="text-center py-12">
              <DocumentArrowDownIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500 font-medium">No hay assessments para este cliente</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr style={{ background: '#eef2f7' }}>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase" style={{ color: '#1B2B6B' }}>Fecha</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase" style={{ color: '#1B2B6B' }}>Estado</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase" style={{ color: '#1B2B6B' }}>Origen</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase" style={{ color: '#1B2B6B' }}>Cuentas</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase" style={{ color: '#1B2B6B' }}>Ahorro potencial</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase" style={{ color: '#1B2B6B' }}>Reporte</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {assessments.map((a) => (
                    <AssessmentRow key={a.id} assessment={a} onReportUrlUpdate={handleReportUrlUpdate} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
}

function AssessmentRow({ assessment, onReportUrlUpdate }: { 
  assessment: Assessment & { results?: { savingsSummary?: { totalMonthlySavings?: number } } };
  onReportUrlUpdate: (id: string, url: string) => void;
}) {
  const savings = assessment.results?.savingsSummary?.totalMonthlySavings;
  const [regenerating, setRegenerating] = useState(false);

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      const result = await regenerateReport(assessment.id);
      onReportUrlUpdate(assessment.id, result.reportUrl);
    } catch (err) {
      console.error('Error regenerating report', err);
    } finally {
      setRegenerating(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!assessment.reportUrl) return;
    try {
      const response = await fetch(assessment.reportUrl);
      const htmlContent = await response.text();
      
      const blob = new Blob([htmlContent], { type: 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'reporte-optimizacion-costos-aws.html';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading report', err);
    }
  };

  return (
    <tr className="transition-colors" style={{ cursor: 'default' }}
      onMouseEnter={e => (e.currentTarget as HTMLTableRowElement).style.background = '#f8fafc'}
      onMouseLeave={e => (e.currentTarget as HTMLTableRowElement).style.background = 'white'}
    >
      <td className="px-4 py-3 text-sm" style={{ color: '#1B2B6B' }}>
        <span className="font-medium">{formatDate(assessment.startedAt)}</span>
        {assessment.completedAt && (
          <div className="text-xs text-gray-400 mt-0.5">
            Completado: {formatDate(assessment.completedAt)}
          </div>
        )}
      </td>
      <td className="px-4 py-3">
        <StatusBadge status={assessment.status} />
      </td>
      <td className="px-4 py-3 text-sm text-gray-600">
        {assessment.triggeredBy === 'SCHEDULER' ? (
          <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full" style={{ background: '#eff6ff', color: '#2563eb' }}>
            <ClockIcon className="h-3.5 w-3.5" />
            Automático
          </span>
        ) : (
          <span className="text-xs text-gray-500">Manual</span>
        )}
      </td>
      <td className="px-4 py-3 text-sm text-gray-600">
        {assessment.accounts && assessment.accounts.length > 1 ? (
          <div>
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: '#f0fdf4', color: '#16a34a' }}>
              {assessment.accounts.length} cuentas
            </span>
            <div className="mt-1 text-xs text-gray-400">
              {assessment.accounts.map((a) => a.label).join(' · ')}
            </div>
          </div>
        ) : assessment.accounts && assessment.accounts.length === 1 ? (
          <span className="text-xs text-gray-500">{assessment.accounts[0].label}</span>
        ) : (
          <span className="text-xs text-gray-400">—</span>
        )}
      </td>
      <td className="px-4 py-3 text-sm font-bold" style={{ color: savings ? '#16a34a' : '#9ca3af' }}>
        {savings !== undefined ? `$${formatCurrency(savings)}` : '—'}
      </td>
      <td className="px-4 py-3">
        {assessment.reportUrl ? (
          <div className="flex items-center gap-2">
            <a
              href={assessment.reportUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-lg transition-all"
              style={{ background: 'linear-gradient(135deg, #E05A1C, #f07030)', color: 'white' }}
            >
              <DocumentArrowDownIcon className="h-4 w-4" />
              Ver Reporte
            </a>
            <button
              onClick={handleDownloadPdf}
              className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border transition-all hover:shadow-sm"
              style={{ borderColor: '#1B2B6B', color: '#1B2B6B' }}
              title="Descargar PDF"
            >
              <CloudArrowDownIcon className="h-3.5 w-3.5" />
              PDF
            </button>
            {assessment.status === 'COMPLETED' && (
              <button
                onClick={handleRegenerate}
                disabled={regenerating}
                className="inline-flex items-center gap-1 text-xs px-2 py-1.5 rounded-lg border transition-all"
                style={{ borderColor: '#dde4f0', color: '#5B8DB8' }}
                title="Regenerar URL del reporte"
              >
                <ArrowPathIcon className={`h-3.5 w-3.5 ${regenerating ? 'animate-spin' : ''}`} />
                {regenerating ? '...' : 'Renovar'}
              </button>
            )}
          </div>
        ) : assessment.status === 'COMPLETED' ? (
          <button
            onClick={handleRegenerate}
            disabled={regenerating}
            className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-lg transition-all"
            style={{ background: 'linear-gradient(135deg, #1B2B6B, #2a4a8a)', color: 'white' }}
          >
            <ArrowPathIcon className={`h-4 w-4 ${regenerating ? 'animate-spin' : ''}`} />
            {regenerating ? 'Generando...' : 'Regenerar reporte'}
          </button>
        ) : (
          <span className="text-sm text-gray-400">—</span>
        )}
      </td>
    </tr>
  );
}
